// Delegate — the headless hook moved to `@heidi/workspace/data` (OpenSpec
// `monorepo-workspace-data-core` · OT3 slice 2, ARCFE-955). This file owns
// both app inputs: the `translateRoles` data transform (i18n-dependent — the
// cached shape is unchanged for identical translations) and the AxiosError
// toast (ARCFE-921), byte-identical copy incl. the detail extraction.
import { toast } from '@heidi/design-system/toast';
import {
  useQueryRoles as useQueryRolesCore,
  type WorkspaceHttpError,
} from '@heidi/workspace/data';

import { useTranslationHelpers } from '@/hooks/useTranslationHelpers';
import { extractAxiosErrorDetail } from '@/utils/axios-error';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { useTranslations } from '@heidi/runtime/i18n';
import { sanitizeError } from '@heidi/runtime/logger';

// Definition moved to `@heidi/workspace/contracts` (OT3 slice 2); import +
// re-export because consumers key caches off it through this path.
import { GET_ROLES } from '@heidi/workspace/contracts';

export { GET_ROLES };

export function useQueryRoles(teamId?: string) {
  const { t } = useTranslations();
  const { translateRoles } = useTranslationHelpers();

  return useQueryRolesCore(teamId, {
    translateRoles,
    onFetchError: (error: WorkspaceHttpError) => {
      // Logged unconditionally, then the toast is guarded: a boot-time
      // reachability failure is expected noise on the screen and still a fact in
      // the log channel. The package does not log this one, so the line lives
      // here or nowhere.
      //
      // Projected through `sanitizeError` first: error-level logs are forwarded
      // to Datadog with their extra args intact, and pino's redaction runs only
      // on the local sink, so handing over the raw axios error would export the
      // request URL, the Authorization header and the response body. Status and
      // code are the diagnostic fields the logger's PHI contract allows.
      logger.error('Roles request failed', sanitizeError(error));
      if (shouldSuppressDesktopOfflineNetworkErrorToast(error)) return;

      toast({
        tone: 'destructive',
        title: t('common.error'),
        description: extractAxiosErrorDetail(error) ?? error.message,
      });
    },
  });
}
